import type { ThemeName } from './colors.ts'

/**
 * Elevation — docs/04-design-system.md §2.5.
 *
 * Shadows are expensive on low-end Android. Only three levels exist, and in
 * dark theme elevation is expressed by SURFACE COLOUR, not by shadow.
 */
export type ElevationLevel = 'flat' | 'raised' | 'overlay'

export type ElevationStyle = {
  /** Semantic colour token to paint the surface with, if any. */
  surface: 'bgSurface' | 'bgSurfaceRaised' | null
  /** Whether the level draws a 1 pt `borderDefault` outline. */
  border: boolean
  shadow: {
    offsetY: number
    blur: number
    color: string
  } | null
}

export const lightElevation = {
  flat: { surface: null, border: false, shadow: null },
  raised: {
    surface: 'bgSurface',
    border: false,
    shadow: { offsetY: 1, blur: 3, color: 'rgba(0,0,0,0.08)' },
  },
  overlay: {
    surface: 'bgSurfaceRaised',
    border: false,
    shadow: { offsetY: 8, blur: 24, color: 'rgba(0,0,0,0.14)' },
  },
} as const satisfies Record<ElevationLevel, ElevationStyle>

export const darkElevation = {
  flat: { surface: null, border: false, shadow: null },
  raised: { surface: 'bgSurfaceRaised', border: false, shadow: null },
  overlay: { surface: 'bgSurfaceRaised', border: true, shadow: null },
} as const satisfies Record<ElevationLevel, ElevationStyle>

export const elevation: Record<ThemeName, Record<ElevationLevel, ElevationStyle>> = {
  light: lightElevation,
  dark: darkElevation,
}

export function getElevation(theme: ThemeName, level: ElevationLevel): ElevationStyle {
  return elevation[theme][level]
}
